import { addDays, isBefore, parseISO } from 'date-fns'
import { makeAutoObservable, makeObservable, observable } from 'mobx'

import { EMPTY_NUMBER_ID } from '@/constant/common.ts'
import { DTO } from '@/dto'
import type { StrapiDataT } from '@/types/common/api'
import { CheckSet } from '@/utils/models'

const POPUP_TYPE = {
  UPDATE_PRODUCT: '기능',
  UPDATE_CONTENTS: '콘텐츠',
  GENERAL: '공지',
  EVENT: '이벤트',
  URGENT: '긴급',
  ETC: '기타',
}

export class NoticeBoard extends DTO {
  id = EMPTY_NUMBER_ID
  title = ''
  content = ''
  memo = ''
  publishDateTime: Date = new Date()
  type: res.NoticeBoard['type'] = 'GENERAL'
  pinEndDateTime: Date | null = null
  mediaUrl: string | null = null
  popupLinkButton: res.NoticeBoard['popupLinkButton'] = null

  constructor(data: StrapiDataT<res.NoticeBoard>) {
    super()

    makeObservable(this, {
      id: observable,
      title: observable,
      content: observable,
      memo: observable,
      publishDateTime: observable,
      type: observable,
      pinEndDateTime: observable,
      mediaUrl: observable,
      popupLinkButton: observable,
    })

    this.id = data.id
    this.title = data.title
    this.content = data.content
    this.memo = data.memo
    this.type = data.type
    this.publishDateTime = parseISO(data.publishedAt)
    this.popupLinkButton = data.popupLinkButton

    if (data.pinEnd) {
      // 핀이 끝나야 하는 날짜는 설정한 날짜보다 하루 뒤여야 하기 때문에.
      this.pinEndDateTime = addDays(new Date(data.pinEnd), 1)
    }

    if (data.media) {
      this.mediaUrl = data.media.data?.[0]?.attributes.url ?? null
    }
  }

  get korTag() {
    return POPUP_TYPE[this.type]
  }

  get isNew() {
    // 7일동안 신규게시물
    const endDate = addDays(this.publishDateTime, 7)
    const today = new Date()

    return isBefore(today, endDate)
  }

  get isPinned() {
    if (!this.pinEndDateTime) return false

    const today = new Date()

    return isBefore(today, this.pinEndDateTime)
  }

  get mediaType() {
    if (!this.mediaUrl) return 'NONE'
    return this.mediaUrl.includes('.mp4') ? 'VIDEO' : 'IMAGE'
  }
}

export class NoticeBoardColl {
  _list: CheckSet<NoticeBoard>
  _MAX_PINNED_SIZE = 3
  _MAX_ITEM_SIZE = 20

  constructor(data: StrapiDataT<res.NoticeBoard>[]) {
    makeAutoObservable(this)
    const allList = data.map((d) => new NoticeBoard(d))
    const pinnedList = allList.filter((notice) => notice.isPinned).slice(0, this._MAX_PINNED_SIZE)

    this._list = new CheckSet(allList, pinnedList)
  }

  get list() {
    return [...this._list.toArray, ...this._list.toUnCheckedArray].slice(0, this._MAX_ITEM_SIZE)
  }

  isPinned(notice: NoticeBoard) {
    return this._list.isChecked(notice)
  }
}
